import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { Camera } from 'lucide-react'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import { CashFirmSelect } from '@/features/cash/shared'
import {
  AccuracyTrend,
  CategoryChart,
  ThresholdInput,
  VarianceSummaryCards,
} from '@/features/cash/VarianceSections'
import {
  DEFAULT_THRESHOLD_PCT,
  diffDirection,
  diffOf,
  exceedsThreshold,
  parseThreshold,
  signedDiff,
} from '@/features/cash/variance-view'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateOnly, formatDateTime, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import { cashApi } from '@/services/http/cash'

const t = messages.cash.variance
const money = (v: number | null | undefined) => (v == null ? '–' : formatDecimal(v, 0))
const pct = (v: number | null | undefined) =>
  v == null ? '–' : `${v > 0 ? '+' : ''}${formatDecimal(v, 1)}%`

/** /nakit/plan-gerceklesen — dondurulmuş tahmin ile DİA gerçekleşeni (docs/NAKIT-PLANI-PLAN.md Faz 6). */
export function VariancePage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const query = useQueryState()
  const week = query.get('hafta')
  // Görünüm tercihleri (URL'de, kaydedilmez): vurgu eşiği ve doğruluk grafiğinin ölçütü.
  const thresholdPct = parseThreshold(query.get('esik'))
  const metric = query.get('olcut') === 'bias' ? 'bias' : 'mape'
  const setThreshold = useCallback(
    (next: number) =>
      query.set({ esik: next === DEFAULT_THRESHOLD_PCT ? undefined : String(next) }),
    [query],
  )
  const canPlan = can('cash.plan')
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const data = useQuery({
    queryKey: queryKeys.cash.variance(firm ?? '', week ?? ''),
    queryFn: () => cashApi.variance(firm!, week),
    enabled: !!firm,
  })
  const snap = useMutation({
    mutationFn: () => cashApi.takeSnapshot(firm!),
    onSuccess: () => {
      toast({ type: 'success', title: t.snapshotTaken })
      void invalidate()
    },
    onError: (e) =>
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(e).detail ?? undefined,
      }),
  })
  const v = data.data

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={
          <>
            <CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />
            {v && v.week_ends.length > 0 && (
              <OptionSelect
                value={v.week_end ?? ''}
                options={v.week_ends.map((w) => ({ value: w, label: formatDateOnly(w) }))}
                onValueChange={(x) => query.set({ hafta: x })}
                aria-label={t.week}
                className="w-40"
              />
            )}
            {canPlan && (
              <Button variant="secondary" loading={snap.isPending} onClick={() => snap.mutate()}>
                <Camera />
                {t.snapshot}
              </Button>
            )}
          </>
        }
      />
      {data.isError ? (
        <ErrorState error={data.error} onRetry={() => void data.refetch()} />
      ) : !v ? (
        <Loading rows={6} />
      ) : (
        <>
          {!v.snapshot_at ? (
            <Alert tone="info" title={t.noSnapshot} />
          ) : (
            <p className="text-text-secondary text-xs">
              {t.snapshotInfo(
                formatDateTime(v.snapshot_at),
                t.reasons[v.snapshot_reason ?? ''] ?? v.snapshot_reason ?? '',
              )}
              {v.week_start && v.week_end && (
                <>
                  {' '}
                  · {formatDateOnly(v.week_start)} – {formatDateOnly(v.week_end)}
                </>
              )}
            </p>
          )}
          {!v.actual_complete && v.snapshot_at && <Alert tone="warning" title={t.incomplete} />}
          {v.snapshot_at && (
            <>
              <VarianceSummaryCards variance={v} thresholdPct={thresholdPct} />
              <CategoryChart categories={v.categories} />
            </>
          )}
          <section className="bg-surface rounded-lg border p-3" aria-label={t.categories}>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">{t.categories}</h2>
              <ThresholdInput value={thresholdPct} onChange={setThreshold} />
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b text-left text-xs">
                  <th className="py-2 pr-3 font-medium">{t.category}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t.forecast}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t.actual}</th>
                  <th className="py-2 pr-3 text-right font-medium">{t.diff}</th>
                  <th className="py-2 text-right font-medium">{t.deviation}</th>
                </tr>
              </thead>
              <tbody>
                {v.categories.map((c) => (
                  <tr key={c.key} className="border-b last:border-0" data-key={c.key}>
                    <td className="py-2 pr-3">{t.kinds[c.key] ?? c.key}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(c.forecast)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{money(c.actual)}</td>
                    <td
                      className="py-2 pr-3 text-right tabular-nums"
                      title={t.directions[diffDirection(diffOf(c))]}
                    >
                      {signedDiff(diffOf(c))}
                    </td>
                    <td
                      className={cn(
                        'py-2 text-right tabular-nums',
                        exceedsThreshold(c.deviation_pct, thresholdPct) &&
                          'text-warning font-semibold',
                      )}
                      data-testid="cash-variance-pct"
                    >
                      {pct(c.deviation_pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-text-secondary mt-2 text-xs">{t.diffHint}</p>
          </section>
          {v.caris.length > 0 && (
            <section className="bg-surface rounded-lg border p-3" aria-label={t.caris}>
              <h2 className="mb-2 text-sm font-semibold">{t.caris}</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary border-b text-left text-xs">
                    <th className="py-2 pr-3 font-medium">{t.cari}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t.forecast}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t.actual}</th>
                    <th className="py-2 text-right font-medium">{t.diff}</th>
                  </tr>
                </thead>
                <tbody>
                  {v.caris.map((c) => (
                    <tr key={c.code} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        {c.name ?? c.code}
                        {c.name && (
                          <span className="text-text-secondary block text-xs">{c.code}</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(c.forecast)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(c.actual)}</td>
                      <td
                        className="py-2 text-right tabular-nums"
                        title={t.directions[diffDirection(diffOf(c))]}
                      >
                        {signedDiff(diffOf(c))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {v.accuracy.length > 0 && (
            <AccuracyTrend
              accuracy={v.accuracy}
              metric={metric}
              onMetricChange={(next) => query.set({ olcut: next === 'mape' ? undefined : next })}
            />
          )}
          {v.accuracy.length > 0 && (
            <section className="bg-surface rounded-lg border p-3" aria-label={t.accuracy}>
              <h2 className="mb-2 text-sm font-semibold">{t.accuracy}</h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-text-secondary border-b text-left text-xs">
                    <th className="py-2 pr-3 font-medium">{t.category}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t.horizon}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t.samples}</th>
                    <th className="py-2 pr-3 text-right font-medium">{t.bias}</th>
                    <th className="py-2 text-right font-medium">{t.mape}</th>
                  </tr>
                </thead>
                <tbody>
                  {v.accuracy.map((a) => (
                    <tr key={`${a.category}-${a.horizon}`} className="border-b last:border-0">
                      <td className="py-2 pr-3">{t.kinds[a.category] ?? a.category}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{a.horizon}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{a.samples}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{money(a.bias)}</td>
                      <td className="py-2 text-right tabular-nums">
                        {a.mape == null ? '–' : `${formatDecimal(a.mape, 1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
          {v.changes && (
            <section className="bg-surface rounded-lg border p-3" aria-label={t.changes}>
              <h2 className="text-sm font-semibold">{t.changes}</h2>
              <p className="text-text-secondary mb-2 text-xs">
                {t.changesSince(formatDateTime(v.changes.previous_at))}
              </p>
              <ul className="grid gap-1 text-sm sm:grid-cols-2">
                {v.changes.closing.map((c) => (
                  <li key={c.end} className="flex justify-between gap-2 tabular-nums">
                    <span>{messages.cash.weekly.week(formatDateOnly(c.end).slice(0, 5))}</span>
                    <span className={cn(c.now - c.before < 0 && 'text-danger')}>
                      {money(c.before)} → {money(c.now)}
                    </span>
                  </li>
                ))}
              </ul>
              {v.changes.caris.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1 text-sm">
                  {v.changes.caris.map((c) => (
                    <li key={c.code} className="flex justify-between gap-2 tabular-nums">
                      <span>{c.name ?? c.code}</span>
                      <span>
                        {money(c.before)} → {money(c.now)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}
